import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { KsefInvoice, KsefInvoiceStatus } from '../models/ksef-invoice.model';

const KSEF_INVOICES_URL = 'ksef/invoices';

@Injectable({
  providedIn: 'root',
})
export class KsefApiService {
  private readonly baseUrl = environment.url;

  constructor(private http: HttpClient) {}

  getGeneralInvoices(): Observable<KsefInvoice[]> {
    return this.fetchInvoices({ scope: 'general' });
  }

  getMyInvoices(): Observable<KsefInvoice[]> {
    return this.fetchInvoices({ scope: 'my' });
  }

  getCancelledInvoices(): Observable<KsefInvoice[]> {
    return this.fetchInvoices({ scope: 'cancelled' });
  }

  getInCirculationInvoices(): Observable<KsefInvoice[]> {
    return this.fetchInvoices({ scope: 'in_circulation' });
  }

  getHrnestSettledInvoices(): Observable<KsefInvoice[]> {
    return this.fetchInvoices({ scope: 'hrnest_settled' });
  }

  private fetchInvoices(params: { scope: string }): Observable<KsefInvoice[]> {
    return this.http
      .get<any[]>(this.baseUrl + KSEF_INVOICES_URL, { params })
      .pipe(
        map((rows) => (Array.isArray(rows) ? rows : []).map((row) => this.mapRow(row))),
        catchError((err) => {
          console.warn('[KsefApiService] Błąd pobierania faktur:', err && err.status, err && err.message);
          return of([]);
        }),
      );
  }

  getInvoice(id: number): Observable<KsefInvoice> {
    return this.http
      .get<any>(this.baseUrl + KSEF_INVOICES_URL + '/' + id)
      .pipe(map((row) => this.mapRow(row)));
  }

  assignInvoice(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}ksef/${id}/assign`, {});
  }

  unassignInvoice(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}ksef/${id}/unassign`, {});
  }

  cancelHrnestInvoice(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}ksef/${id}/cancel-hrnest`, {});
  }

  requestReturnInvoice(id: number): Observable<void> {
    const response = this.http.post<void>(`${this.baseUrl}ksef/${id}/request-return`, {});
    console.log(response);
    return response;
  }

  private mapRow(row: any): KsefInvoice {
    return {
      id: row.id,
      costId: row.costId != null ? row.costId : null,
      ksefInvoiceId: row.ksefInvoiceId,
      costCreatorName: row.costCreatorName,
      vendorInvoiceNumber: row.vendorInvoiceNumber,
      ksefNumber: row.ksefNumber,
      issueDate: row.issueDate,
      dueDate: row.dueDate || null,
      grossAmount: row.grossAmount != null ? row.grossAmount : null,
      currency: row.currency || 'PLN',
      contractorName: row.contractorName != null ? row.contractorName : null,
      contractorNip: row.contractorNip != null ? row.contractorNip : null,
      employeeIdentifier: row.employeeIdentifier != null ? row.employeeIdentifier : null,
      isPaid: !!row.isPaid,
      status: (row.status as KsefInvoiceStatus) || KsefInvoiceStatus.PENDING_ASSIGNMENT,
      attachmentUrl: row.attachmentUrl || null,
      attachmentFilename: row.attachmentFilename || null,
      isReturnRequested: !!row.isReturnRequested,
      createdAt: row.createdAt || null,
    };
  }
}

